/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type AfterViewInit, type OnDestroy, Component, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { EventService, I18nService, TooltipDirective } from '@dm/core';
import { GlobalDataService } from 'app/common/services';
import { type Alarm, type AlarmCountAndRecent } from 'app/common/types/mo';
import { SWITCH } from 'app/config';
import { type Subscription } from 'rxjs';

import { AlarmsAndEventsService } from '../alarms-and-events.service';
import { AlarmsAndEventsStrService } from '../alarms-and-events-str.service';

@Component({
    selector: 'app-alarm-panel',
    templateUrl: './alarms-panel.component.html',
    styleUrls: ['./alarms-panel.component.scss'],
})
export class AlarmsPanelComponent implements AfterViewInit, OnDestroy {
    @ViewChild('alarmsSetting') alarmsSetting;
    @ViewChild('alarmTooltip', { read: TooltipDirective }) tooltip: TooltipDirective;

    tooltipVisible = false;

    isShowAlarmSetting = false;
    i18n = {
        viewAllAlarm: this.i18nService.get('monitor.recentAlarm_viewAllAlarm_lab'),
        noData: this.i18nService.get('monitor.recentTasks_result_noData'),
        recentAlarm: this.str.recentAlarm,
        alarmTip: this.i18nService.get('common.Alarms'),
        turnOnSound: this.i18nService.get('monitor.recentAlarm_viewAllAlarm_voiceSwitch_enable_tip'),
        turnOffSound: this.i18nService.get('monitor.recentAlarm_viewAllAlarm_voiceSwitch_disable_tip'),
        soundSetting: this.i18nService.get('monitor.recentAlarm_viewAllAlarm_voiceSetting_tip'),
    };

    // 最新10条告警列表对象
    recent = {
        isLoading: false,   // 是否正在加载数据
        noData: false,      // 后台无数据返回，显示暂无数据
        alarms: [],           // 保存数据的数组
        alarmsCache: [],
    };

    // 告警等级
    alarmLevel: any;

    // 告警声音开关
    alarmVoiceEnabled: boolean;

    // 告警声音级别控制
    minAlarmLevel: number;

    // 告警声音对象
    audio: HTMLAudioElement;

    // 各类型告警数量
    criticalCount = 0;
    majorCount = 0;
    warningCount = 0;

    total = 0;

    // 告警定时器对象
    voiceTimer = null;

    /**
     * 查看所有告警的路由
     */
    get allAlarmRouter(): string {
        return SWITCH.IS_SIMPLE_MODE ? '/simple/alarms-and-events' : '/monitor/alarms-and-events';
    }

    get isSimpleMode(): boolean {
        return SWITCH.IS_SIMPLE_MODE;
    }

    private sub$: Subscription;

    constructor(
        private i18nService: I18nService,
        private service: AlarmsAndEventsService,
        public str: AlarmsAndEventsStrService,
        private eventService: EventService,
        private globalDataService: GlobalDataService,
        private router: Router,
    ) { }

    ngAfterViewInit(): void {
        this.audio = document.getElementById('alarmAudio') as HTMLAudioElement;
        Promise.resolve().then(() => {
            this.initData();
        });

        this.sub$ = this.router.events.subscribe((evt) => {
            if (evt instanceof NavigationEnd && this.tooltipVisible) {
                this.tooltipVisible = false;
            }
        });
    }

    ngOnDestroy(): void {
        this.sub$.unsubscribe();
    }

    initData(): void {
        this.eventService.subscribe('alarmUpdate', (data) => {
            this.updateData(data);
        });
        this.updateData(this.globalDataService.getAlarmData());
    }

    updateData({ critical, major, warning, recent }: AlarmCountAndRecent): void {
        this.criticalCount = critical;
        this.majorCount = major;
        this.warningCount = warning;
        this.total = critical + major + warning;
        this.updateRecentList(recent);
        this.initAlarmVoice();
    }

    updateRecentList(resArr): void {
        const dataObj = this.recent;
        if (resArr.length !== 0) {
            // 渲染时间
            resArr.forEach((element) => {
                element.startTimeStr = this.service.renderTime(element.startTime);
            });
            dataObj.alarms = dataObj.alarmsCache = resArr;
            if (dataObj.alarms && dataObj.alarms.length > 0) {
                dataObj.noData = false;
            } else {
                dataObj.noData = true;
            }
        } else {
            dataObj.alarms = [];
            dataObj.noData = true;
        }
    }

    initAlarmVoice(): void {
        this.getAlarmsCookies();
        let alarmVoice: boolean;
        if (!this.alarmVoiceEnabled || (!this.criticalCount && !this.majorCount && !this.warningCount)) {
            alarmVoice = false;
        } else {
            if (this.alarmVoiceEnabled) {
                const isLevel3Voice = this.minAlarmLevel === 3 && (this.criticalCount > 0 || this.majorCount > 0 || this.warningCount > 0);
                if (this.minAlarmLevel === 6 && this.criticalCount > 0) {
                    alarmVoice = true;
                } else if (this.minAlarmLevel === 5 && (this.criticalCount > 0 || this.majorCount > 0)) {
                    alarmVoice = true;
                } else if (isLevel3Voice) {
                    alarmVoice = true;
                } else {
                    alarmVoice = false;
                }
            }
        }
        if (alarmVoice) {
            if (!this.voiceTimer) {
                // 立即播放一次
                this.audio.play();
                this.voiceTimer = window.setInterval(() => {
                    this.audio.play();
                }, 1500);
            }
        } else {
            if (this.voiceTimer) {
                window.clearInterval(this.voiceTimer);
                this.voiceTimer = null;
            }
            this.audio?.pause();
        }
    }

    getAlarmsCookies(): void {
        let alarmSettings = this.service.getAlarmsSettings();
        if (alarmSettings[0] === '' && alarmSettings[0] === '') {
            alarmSettings = [false, 3];
            this.service.setAlarmsSettings(alarmSettings[0], alarmSettings[1]);
        }
        [this.alarmVoiceEnabled, this.minAlarmLevel] = alarmSettings;
    }

    viewAlarm(alarm: Alarm): void {
        this.router.navigate([this.allAlarmRouter], {
            queryParams: {
                sequence: alarm.sequence,
            },
            state: alarm,

        });
        this.tooltipVisible = false;
    }

    viewAllAlarm(): void {
        this.tooltip.hide();
    }

    viewAlarmsSetting(): void {
        this.isShowAlarmSetting = true;
        this.tooltip.hide();
    }

    onSettingSave(): void {
        this.isShowAlarmSetting = false;
        this.initAlarmVoice();
    }

    soundSwitchClick(): void {
        this.alarmVoiceEnabled = !this.alarmVoiceEnabled;
        this.service.setAlarmsSettings(this.alarmVoiceEnabled, this.minAlarmLevel);
        this.initAlarmVoice();
    }
}
